
import { Box, Link, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SchoolOutlined } from '@mui/icons-material';
import { ActionItemStatus, ProviderActivationEventName } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useProviderActivationEvents } from '@uplift/queries';
import { LightChip, LinkDashboardActionItem } from '@uplift/ui-v2';

import { TRAINING_TYPEFORM_URL } from '../../constants';
import { getChipColor, hasEvent, openInNewTab } from '../../helpers';
import { useSharedStyles } from '../../styles';
import { useOnboardingEventActions } from '../../useOnboardingEventActions.hook';

export type UpliftTrainingProps = {
  status: ActionItemStatus;
};

export const UpliftTraining: React.VFC<UpliftTrainingProps> = ({ status }) => {
  const l10n = useL10n();
  const { classes: sharedClasses } = useSharedStyles();
  const { classes } = useStyles();
  const user = useCurrentuser();
  const { data: events, isLoading: eventsIsLoading } = useProviderActivationEvents(user?.id);
  const { providerTrainingStarted, providerTrainingSubmitted } = useOnboardingEventActions(user?.id);

  if (eventsIsLoading) return null;

  const showMarkAsDone = hasEvent(events, ProviderActivationEventName.trainingStarted);

  const handleGoToTraining = () => {
    if (!hasEvent(events, ProviderActivationEventName.trainingStarted)) {
      providerTrainingStarted();
    }
    openInNewTab(TRAINING_TYPEFORM_URL);
  };

  if (status === ActionItemStatus.todo) {
    return (
      <LinkDashboardActionItem
        title={l10n.dashboard.actionItems.completeUpliftTraining.title}
        body={l10n.dashboard.actionItems.completeUpliftTraining.subtitle}
        bodyClassName={sharedClasses.actionItemSubtitle}
        color="primary"
        icon={<SchoolOutlined />}
        link="#"
        actionText={l10n.providerSettings.onboarding.goToTraining}
        dismiss={showMarkAsDone && {
          text: l10n.providerSettings.onboarding.markAsDone,
          handleClick: providerTrainingSubmitted
        }}
        onClick={handleGoToTraining}
      />
    );
  }

  return (
    <Box className={sharedClasses.progressSummaryStepWithChip}>
      <Box display="flex" flexDirection="column">
        <Typography variant="h5">{l10n.providerSettings.onboarding.upliftTraining}</Typography>
        <Box className={classes.trainingLinks}>
          <Link href={TRAINING_TYPEFORM_URL} target="_blank" rel="noreferrer" className={sharedClasses.link}>{l10n.providerSettings.onboarding.redoTraining}</Link>
          {/* TODO: Update link to Get Started guide*/}
          {/* <FiberManualRecord className={classes.dotIcon} /> */}
          {/* <Link href={TRAINING_TYPEFORM_URL} target="_blank" rel="noreferrer" className={sharedClasses.link}>{l10n.providerSettings.onboarding.reviewGetStartedGuide}</Link> */}
        </Box>
      </Box>
      <LightChip label={l10n.dashboard.actionItems.status[status]} color={getChipColor(status)} size="small" />
    </Box>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  trainingLinks: {
    display: 'flex',
    gridGap: theme.spacing(0.8),
    alignItems: 'center'
  },
  dotIcon: {
    width: '5px',
    height: '5px'
  }
}));
